import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

// ADR-0001: custom REST cache for ContentSpace (react-query not installed in monorepo).
// Contract: get dedupes concurrent fetches by key; invalidate(prefix) bumps revision for matching keys
// and drops cached values; hooks re-subscribe and re-fetch. Keys — see hooks.ts / architecture.md §5.3.

interface ICacheEntry<T = unknown> {
  promise?: Promise<T>
  value?: T
  error?: Error
  revision: number
}

export interface IContentSpaceRestCache {
  get<T>(key: string, fetcher: () => Promise<T>): Promise<T>
  set<T>(key: string, value: T): void
  invalidate(prefix: string): void
  refresh(prefix: string): void
  subscribe(key: string, cb: () => void): () => void
  getRevision(key: string): number
  peek<T>(key: string): ICacheEntry<T> | undefined
}

const ContentSpaceRestContext = createContext<IContentSpaceRestCache | null>(null)

// Провайдер и fallback должны **шарить один Map**, иначе:
// - ModalProvider рендерит модалки в своей поддереве (вне ContentSpaceRoutes),
//   где `useContext(ContentSpaceRestContext)` → null → падение на fallback;
// - страница портала внутри ContentSpaceRoutes получает контекстную копию.
// При `cache.invalidate(...)` из модалки — страница продолжает читать
// **чужой** кеш со стейлом. См. POR-004-ADMIN: после `setCoursePortals` портал
// не показывает привязанный курс, хотя BE и FE-fetch говорят что всё ок.
// Решение: провайдер отдаёт через `value={sharedCache}` ту же singleton-инстанцию,
// которой пользуется `useContentSpaceRestCache()` без провайдера.
export const ContentSpaceRestProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ContentSpaceRestContext.Provider value={fallbackCache}>
      {children}
    </ContentSpaceRestContext.Provider>
  )
}

// Fallback cache when provider is not mounted (e.g. legacy call-sites outside ContentSpace routes).
// Same interface, module-scoped singleton — still gives dedupe/invalidate.
export const createFallbackCache = (): IContentSpaceRestCache => {
  const cache = new Map<string, ICacheEntry>()
  const listeners = new Map<string, Set<() => void>>()
  const notify = (key: string) => listeners.get(key)?.forEach((cb) => cb())
  const getEntry = (key: string): ICacheEntry => {
    let entry = cache.get(key)
    if (!entry) {
      entry = { revision: 0 }
      cache.set(key, entry)
    }
    return entry
  }
  return {
    get<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
      const entry = getEntry(key) as ICacheEntry<T>
      if (entry.promise) {
        return entry.promise
      }
      if (entry.value !== undefined && entry.error === undefined) {
        return Promise.resolve(entry.value)
      }
      const promise = fetcher()
        .then((r) => {
          const c = cache.get(key) as ICacheEntry<T> | undefined
          if (c && c.promise === promise) {
            c.value = r
            c.error = undefined
            c.promise = undefined
            notify(key)
          }
          return r
        })
        .catch((err: unknown) => {
          const c = cache.get(key) as ICacheEntry<T> | undefined
          const e = err instanceof Error ? err : new Error(String(err ?? 'Unknown error'))
          if (c && c.promise === promise) {
            c.error = e
            c.value = undefined
            c.promise = undefined
            notify(key)
          }
          throw e
        })
      entry.promise = promise
      return promise
    },
    set<T>(key: string, val: T) {
      const e = getEntry(key)
      e.value = val
      e.error = undefined
      e.promise = undefined
      e.revision += 1
      notify(key)
    },
    invalidate(prefix: string) {
      const toNotify: string[] = []
      cache.forEach((entry, key) => {
        if (key === prefix || key.startsWith(prefix)) {
          entry.promise = undefined
          entry.value = undefined
          entry.error = undefined
          entry.revision += 1
          toNotify.push(key)
        }
      })
      toNotify.forEach(notify)
    },
    refresh(prefix: string) {
      const toNotify: string[] = []
      cache.forEach((entry, key) => {
        if (key === prefix || key.startsWith(prefix)) {
          entry.promise = undefined
          entry.error = undefined
          entry.revision += 1
          toNotify.push(key)
        }
      })
      toNotify.forEach(notify)
    },
    subscribe(key: string, cb: () => void) {
      let s = listeners.get(key)
      if (!s) {
        s = new Set()
        listeners.set(key, s)
      }
      s.add(cb)
      return () => {
        s?.delete(cb)
      }
    },
    getRevision(key: string) {
      return cache.get(key)?.revision ?? 0
    },
    peek<T>(key: string) {
      return cache.get(key) as ICacheEntry<T> | undefined
    },
  }
}

const fallbackCache = createFallbackCache()

export const useContentSpaceRestCache = (): IContentSpaceRestCache => {
  return useContext(ContentSpaceRestContext) ?? fallbackCache
}

export interface IRestResourceResult<T> {
  data: T | null
  loading: boolean
  isFetching: boolean
  error: Error | null
  refetch: () => void
}

export const useRestResource = <T,>(
  cacheKey: string | null,
  fetcher: () => Promise<T>,
): IRestResourceResult<T> => {
  const cache = useContentSpaceRestCache()
  const [, forceRender] = useState(0)
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  useEffect(() => {
    if (cacheKey === null) {
      return undefined
    }
    const unsub = cache.subscribe(cacheKey, () => {
      // После `invalidate(prefix)` value/promise обнуляются — подписанный
      // компонент только перерендеривается (notify) и остаётся без данных,
      // т.к. второй useEffect не реагирует на revision. Перезапустим fetch
      // вручную на пустой entry.
      const entry = cache.peek<T>(cacheKey)
      if (!entry || (entry.value === undefined && entry.error === undefined && !entry.promise)) {
        cache.get(cacheKey, () => fetcherRef.current()).catch(() => undefined)
      }
      forceRender((v) => v + 1)
    })
    return unsub
  }, [cache, cacheKey])

  useEffect(() => {
    if (cacheKey === null) {
      return
    }
    const entry = cache.peek<T>(cacheKey)
    if (!entry || (entry.value === undefined && entry.error === undefined && !entry.promise)) {
      cache.get(cacheKey, () => fetcherRef.current()).catch(() => undefined)
    }
  }, [cache, cacheKey])

  const refetch = useCallback(() => {
    if (cacheKey === null) {
      return
    }
    cache.refresh(cacheKey)
    fetcherRef
      .current()
      .then((result) => cache.set(cacheKey, result))
      .catch(() => undefined)
  }, [cache, cacheKey])

  const entry = cacheKey !== null ? cache.peek<T>(cacheKey) : undefined
  const isFetching = cacheKey !== null && entry?.promise !== undefined
  const data = entry?.value !== undefined ? entry.value : null
  const loading = cacheKey !== null && data === null && isFetching && !entry?.error
  const error = entry?.error ?? null

  return { data, loading, isFetching, error, refetch }
}
